import clsx from 'clsx'
import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'

import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { setRecentlyUsedAirportCode } from 'actions/AuthActions'
import {
  fetchOfferFlightPrice,
  fetchRelatedTravelItems,
} from 'actions/OfferActions'
import { CLEAR_OFFER_CHECK_IN_CHECK_OUT, SET_OFFER_CHECK_IN_CHECK_OUT } from 'actions/actionConstants'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import Breakpoint from 'components/Common/Breakpoint'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import LayoutContainer from 'components/Common/LayoutContainer'
import OfferPageSignUpWall from 'components/Common/OfferPageSignUpWall'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import WalledContent from 'components/Common/WalledContent'
import ContentTagBanner from 'components/Content/ContentTagBanner'
import AccommodationOfferPageStickyFooter from 'components/OfferPage/Accommodation/AccommodationOfferPageStickyFooter'
import AccommodationOfferPageStickyHeaderMobile from 'components/OfferPage/Accommodation/AccommodationOfferPageStickyHeaderMobile'
import SinglePropertyHotelFlightsToggleButton from 'components/OfferPage/Accommodation/HotelFlightsToggle/SinglePropertyHotelFlightsToggleButton'
import AlwaysOnMobileStickyHeaderFilters from 'components/OfferPage/AlwaysOn/MobileBookingFlow/AlwaysOnMobileStickyHeaderFilters'
import OfferAirportSelectBox from 'components/OfferPage/Filters/Airport/OfferAirportSelectBox'
import OfferCapacityWithAgesSelect from 'components/OfferPage/Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import OfferCapacityWithAgesSelectBox from 'components/OfferPage/Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import OfferDatePickerSelect from 'components/OfferPage/Filters/DatePicker/OfferDatePickerSelect'
import OfferDatePickerSelectBox from 'components/OfferPage/Filters/DatePicker/OfferDatePickerSelectBox'
import type { OfferDurationOption } from 'lib/offer/offerDurationTypes'
import OfferDurationTabs from 'components/OfferPage/Filters/DurationTabs/OfferDurationTabs'
import { shouldShowPropertyDurationTabs } from 'lib/offer/propertyDurationUtils'
import OfferDesktopFlightToggle from 'components/OfferPage/Filters/FlightToggle/OfferDesktopFlightToggle'
import OfferPageLoyaltyPointsSlider from 'components/OfferPage/Loyalty/OfferPageLoyaltyPointsSlider'
import OfferPageFlightDealBanner from 'components/OfferPage/OfferPageFlightDealBanner'
import OfferPageGallery from 'components/OfferPage/OfferPageGallery'
import SocialProofDialog from 'components/OfferPage/SocialProofDialog'
import ExpiredDealsLereRecommendations from 'components/Recommendations/ExpiredDeals/ExpiredDealsLereRecommendations'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import Backdrop from 'components/utils/Backdrop'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import { verticalChildSpacing } from 'components/utils/spacing'
import config from 'constants/config'
import { Experiments } from 'constants/experiments'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import {
  metasearchRateContextQueryParams,
  urlHashDatePicker,
} from 'constants/url'
import ModalProvider from 'contexts/ModalProvider'
import type {
  OfferPageFilterType,
  OfferPageMultiRoomMode,
  OfferPageRoom,
} from 'contexts/OfferPage/offerPageStateReducer'
import {
  OfferPageActions,
  initialOfferPageRooms,
} from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import GeoContext from 'contexts/geoContext'
import useExperiment from 'hooks/Experiments/useExperiment'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { useAvailabilityForDate } from 'hooks/OfferPage/useAvailabilityForDate'
import { getLereDynamicPricingGroup, getLereDynamicPricingGroupForOffer } from 'checkout/lib/utils/lereDynamicPricing'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import usePropertyIndividualRoomAutoselection from 'hooks/OfferPage/usePropertyIndividualRoomAutoselection'
import { SOCIAL_PROOF_COPY_VARIANTS } from 'hooks/OfferPage/useSocialProofPopup'
import useOfferTags from 'hooks/Offers/useOfferTags'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useFlightPrice from 'hooks/useFlightPrice'
import useMountedEffect from 'hooks/useMountedEffect'
import usePrevious from 'hooks/usePrevious'
import usePropertyCalendarPrices from 'hooks/usePropertyCalendarPrices'
import usePropertyRates from 'hooks/usePropertyRates'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import useShareModalConfig from 'hooks/useShareModalConfig'
import isAuDomesticAirportRoute from 'lib/flights/isAuDomesticAirportRoute'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { countOccupantsForFlights, isValidFlightsOccupants } from 'lib/offer/occupancyUtils'
import { hasSelectedRooms, hasSelectedRoomsOrDefault } from 'lib/offer/offerPageUtil'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import { hasOfferExpired } from 'lib/offer/offerUtils'
import { replace } from 'lib/routing/appRouting'
import {
  doesOfferPageSearchContextMatch,
  encodeSearchParams,
  getMetasearchRoomOptionFromSearch,
  isRoomsInvalid,
} from 'lib/search/searchUtils'
import {
  getCheckoutDate,
  getPropertyLoggedOutSavedPercent,
  getPropertyRatesKey,
  getShouldShowPropertyAnytimeRate,
  isPropertyAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'
import { isSppMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { getDurationOptions } from 'selectors/offerPage/propertyOfferPageSelectors'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'

import PropertyDesktopIndividualRooms from './PropertyDesktopIndividualRooms'
import PropertyIntro from './PropertyIntro'
import { PropertyMultiRoomBookingSectionDesktop } from './PropertyMultiRoomBookingSectionDesktop'
import PropertyPromotionNudgeBanner from './PropertyPromotionNudgeBanner'
import PropertyQuickFilters from './PropertyQuickFilters'
import PropertyRoomOptions from './PropertyRoomOptions'
import PropertySidebarFilters from './PropertySidebarFilters'
import PropertyStickyFooter from './PropertyStickyFooter'
import PropertyStickyHeader from './PropertyStickyHeader'
import PropertyUniqueStaysRecommendations from './PropertyUniqueStaysRecommendations'

const RelativeBlock = styled.form`
  position: relative;
`

const PackageOptionsPane = styled(Pane)`
  padding: ${rem(20)} 0;

  ${verticalChildSpacing[8]};
`

const ResponsivePackageOptionsSpacer = styled.div`
  & > * + * {
    margin-top: ${rem(8)};
  }
  & > [data-large-gap] + * {
    margin-top: ${rem(32)};
  }
`

const QuickFiltersRoomsGroup = styled(Group)`
  && {
    margin-top: ${rem(16)};
  }

  ${mediaQueryUp.tablet} {
    && {
      margin-top: ${rem(32)};
    }
  }
`

// Mirrors LayoutContainer on tablet+ but is a passthrough on mobile so the slider can sit full-width.
const ResponsiveSliderInnerWrapper = styled.div`
  width: 100%;

  ${mediaQueryUp.tablet} {
    padding-inline: ${rem(16)};
    margin-inline: auto;
    max-width: ${rem(1172)};
  }
`

const DesktopFilterBlock = styled(Group)`
  &.open {
    position: relative;
    z-index: 3;
  }
`

const StyledOfferCapacityWithAgesSelect = styled(OfferCapacityWithAgesSelect)`
  flex-grow: 1;
  max-width: ${rem(250)};
`

const StyledOfferDatePickerSelect = styled(OfferDatePickerSelect)`
  flex-grow: 1;
  max-width: ${rem(250)};
`

const FlexDesktopFilterBlock = styled(DesktopFilterBlock)`
  flex-grow: 1;

  &:has(> :nth-child(3)) > * {
    flex: 1;
    max-width: unset;
  }
`

const FullBleedOnMobileContainer = styled(LayoutContainer)`
  ${mediaQueryDown.mobile} {
    padding: 0;
  }
`

interface Props {
  property: App.Property.Property
  /**
   * @deprecated Use `property` instead. (SPP combines multiple offers into a single property; there
   * is no canonical offer.)
   */
  offer: App.HotelOffer
  socialProofRecentPurchases: Array<App.OfferRecentPurchase> | undefined
  socialProofImage: App.Image
  airport?: App.Airport
  duration: number
  activeFilter?: OfferPageFilterType
  activeHeaderFilter?: OfferPageFilterType
  rooms: Array<OfferPageRoom>
  checkInDate?: string
  checkOutDate?: string
  flights?: boolean
  areAnytimeRates: boolean
  shouldShowPropertyAnytimeRates: boolean
  durationOptions: Array<OfferDurationOption>
  availableForPurchase?: boolean
  loggedOutSavedPercent: number | undefined
  multipleRoomBookingActive: boolean
  multiRoomMode: OfferPageMultiRoomMode | undefined
  utmContent: string | undefined
  isAlternativeDatesFlow: boolean
  hasOpenFilter: boolean
}

function PropertyContent(props: Props) {
  const {
    property,
    offer,
    socialProofRecentPurchases,
    socialProofImage,
    airport,
    duration,
    activeFilter,
    rooms,
    checkInDate,
    checkOutDate,
    flights,
    areAnytimeRates,
    shouldShowPropertyAnytimeRates,
    durationOptions,
    availableForPurchase,
    loggedOutSavedPercent,
    multipleRoomBookingActive,
    multiRoomMode,
    activeHeaderFilter,
    utmContent,
    isAlternativeDatesFlow,
    hasOpenFilter,
  } = props
  const destinationAirportCode = property.airportCode

  const intl = useIntl()
  const location = useLocation()
  const { originAirports, airports } = useContext(GeoContext)
  const currentRegionHasFlights = useAppSelector(state => state.geo.currentRegionHasFlights)
  const prevPath = useAppSelector(state => state.routeHistory.prevPath)
  const lereGroup = useAppSelector(state => getLereDynamicPricingGroupForOffer(offer, getLereDynamicPricingGroup(state)))

  const dispatch = useAppDispatch()
  const stateDispatch = useOfferPageDispatch()

  const scrollAfterFilterClose = useRef<boolean>(false)
  const previousRooms = usePrevious(rooms)
  const previousCheckIn = usePrevious(checkInDate)
  const packageOptionsAnchor = useRef<HTMLDivElement>(null)
  const previousFilter = usePrevious(activeFilter)
  const lastFetchedRooms = useRef<Array<App.Occupants>>([])
  const desktopFilterBlockRef = useRef<HTMLDivElement>(null)
  const { tags: offerContentTags } = useOfferTags(offer.id)

  const isWalledGardenEuropeOffersEnabled = useEuropeOfferPageWalledGarden()

  const isMobileDevice = useScreenSizeOnly('mobile')
  // NB: This experiment is currently disabled, and should _not_ be re-enabled until we've rewritten
  // all the mobile flow components.
  const { isAnyVariant: isNewMobileBookingFlowVariant } = useExperiment(
    Experiments.croAlwaysOnNewMobileBooking,
    { canActivate: isMobileDevice },
  )
  const useNewMobileBookingFlow = isMobileDevice && isNewMobileBookingFlowVariant

  const isSppMultiIndividualRoomEnabled = useFeatureFlag(OptimizelyFeatureFlags.sppMultiIndividualRoom)
  // TODO: mobile multi-room flow deferred for V1. AgentHub multi-room deferred for V1.
  const canUseMultiRoomUI = isSppMultiIndividualRoomEnabled && !isAgentHubEnabled && !isMobileDevice
  // Same-room mode falls through to PropertyRoomOptions; only individual mode renders the per-room grid.
  const multiRoomShellActive = canUseMultiRoomUI && rooms.length > 1
  const showIndividualRoomGrid = multiRoomShellActive && multipleRoomBookingActive
  const showMultiRoomBookingSection = multiRoomShellActive

  usePropertyIndividualRoomAutoselection(canUseMultiRoomUI)

  const invalidRoomSelectionForSticky = !hasSelectedRoomsOrDefault(rooms) || isRoomsInvalid(rooms)
  const canViewOptionsForSticky = !!checkInDate && !invalidRoomSelectionForSticky
  const shouldMountMobileAccommodationStickyFooter = useNewMobileBookingFlow && !canViewOptionsForSticky

  const shouldKeepMetasearchRateContext = useMemo(() => doesOfferPageSearchContextMatch(
    location.search,
    checkInDate,
    checkOutDate,
    rooms,
  ), [location.search, checkInDate, checkOutDate, rooms])

  const queryString = useMemo(() =>
    encodeSearchParams({
      urlSearch: location.search,
      dates: {
        checkIn: checkInDate,
        checkOut: checkOutDate,
      },
      rooms,
      flights,
      multiRoomMode,
      paramsToKeep: shouldKeepMetasearchRateContext ? metasearchRateContextQueryParams : undefined,
    }).toString(), [checkInDate, checkOutDate, rooms, location.search, flights, shouldKeepMetasearchRateContext, multiRoomMode])

  // Need to pass the context state above the tree
  useEffect(() => {
    dispatch({
      type: SET_OFFER_CHECK_IN_CHECK_OUT,
      payload: {
        offerId: offer.id,
        checkIn: checkInDate,
        checkOut: checkOutDate,
        duration,
        rooms,
      },
    })

    return
  }, [dispatch, offer.id, checkInDate, duration, rooms, checkOutDate])

  useEffect(() => {
    // clear the check in/check out on unmount
    return () => {
      dispatch({
        type: CLEAR_OFFER_CHECK_IN_CHECK_OUT,
        payload: {
          offerId: offer.id,
        },
      })
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useMountedEffect(() => {
    scrollAfterFilterClose.current = scrollAfterFilterClose.current ||
      previousRooms !== rooms ||
      previousCheckIn !== checkInDate
  }, [rooms, checkInDate])

  useAvailabilityForDate(
    offer.id,
    rooms,
    isAlternativeDatesFlow ? checkInDate : undefined,
    isAlternativeDatesFlow ? duration : undefined,
    lereGroup,
  )

  useIdleTimeReporter(IdleTimeSource.AlwaysOn)

  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const scrollToPackageOptions = useCallback(() => {
    requestAnimationFrame(() => {
      packageOptionsAnchor.current?.scrollIntoView()
    })
  }, [])

  // 'auto' scroll (not from a user action) only occurs on tablet and up sizes
  // as we don't want the users scroll to lose their position on mobile
  const autoScrollToFilters = useCallback(() => {
    if (isTabletScreenSizeAndUp) {
      requestAnimationFrame(() => {
        desktopFilterBlockRef.current?.scrollIntoView({ behavior: 'auto' })
      })
    }
  }, [isTabletScreenSizeAndUp])

  const hasRoomsChanged = useMemo(() => {
    const currentOccupancyNumber = rooms.map(generateOccupancyStringByRoom).join('|')
    const lastOccupancyNumber = (lastFetchedRooms.current ?? initialOfferPageRooms).map(generateOccupancyStringByRoom).join('|')
    return currentOccupancyNumber !== lastOccupancyNumber
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, activeFilter])

  useEffect(() => {
    const hasRooms = hasSelectedRooms(rooms)
    if (previousFilter === 'capacity' && !hasRooms) {
      if (!activeFilter) {
        autoScrollToFilters()
        scrollAfterFilterClose.current = false
      }
    }

    if (activeFilter) {
      autoScrollToFilters()
    }
  }, [activeFilter, activeHeaderFilter, previousFilter, hasRoomsChanged]) // oxlint-disable-line react-hooks/exhaustive-deps

  const roomOption = useMemo(() => getMetasearchRoomOptionFromSearch(
    location.search,
    checkInDate,
    checkOutDate,
    rooms,
  ), [location.search, checkInDate, checkOutDate, rooms])
  useEffect(() => {
    stateDispatch({ type: OfferPageActions.setRoomOption, roomOption })
  }, [stateDispatch, roomOption])

  useEffect(() => {
    dispatch(fetchRelatedTravelItems(offer, checkInDate, checkOutDate, rooms))
  }, [offer, duration, checkInDate, dispatch, rooms, checkOutDate])

  useMountedEffect(() => {
    // effectively listening on 'on duration change' event
    if (!activeFilter) {
      autoScrollToFilters()
    }
  }, [duration])

  useMountedEffect(() => {
    // effectively an 'on airport change' event
    if (airport) {
      dispatch(setRecentlyUsedAirportCode(airport.code))
      dispatch(fetchOfferFlightPrice(offer.id, airport.code))
    }
  }, [airport])

  useEffect(() => {
    if (location.hash === urlHashDatePicker) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }
  }, []) // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(replace({ ...location, search: queryString }))
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString])

  // Suppress GHA highlight params in individual mode; passing them would skew rate ordering and produce a spurious isHighlighted marker.
  usePropertyRates({
    propertyId: property.id,
    checkIn: checkInDate,
    checkOut: checkOutDate,
    occupancies: rooms,
    utmContent,
    roomTypeId: showIndividualRoomGrid ? undefined : roomOption?.roomTypeId,
    roomRateId: showIndividualRoomGrid ? undefined : roomOption?.roomRateId,
    roomRateGroupId: showIndividualRoomGrid ? undefined : roomOption?.roomRateGroupId,
    disabled: hasOpenFilter,
  })
  // Ensure cheapest flights are also loaded. Note that (for now) we'll assume: (i) both LPC and
  // bedbank offers are configured identically (i.e. both have flights enabled and the same
  // destination airport code); and (ii) there's no `forceBundleId` for LPC offers (or its discount
  // is immaterial).
  useFlightPrice(
    destinationAirportCode,
    rooms,
    checkInDate,
    checkOutDate,
    airport?.code,
    duration,
    {
      disabled: !property.areFlightsEnabled,
      exactSabrePrice: !!checkInDate && !!checkOutDate,
    },
  )
  // Prefetch calendar pricing for marketed LoS and room occupancy if there are active dynamic Flash
  // campaign promotions.
  usePropertyCalendarPrices({
    propertyId: property.id,
    duration,
    occupancies: rooms,
    // NB: We also guard on this condition within `fetchPropertyCalendarPrices`.
    disabled: property.promotion?.type !== 'dynamicFlash',
  })

  const occupancyLimits = useMemo(() => {
    return Object.values(property.rooms).flatMap(({ capacities }) => capacities)
  }, [property.rooms])

  const propertyRooms = useMemo(() => Object.values(property.rooms), [property.rooms])

  const isValidFlightsOccupancy = useMemo(() => {
    const flightOccupants = countOccupantsForFlights(rooms)
    return isValidFlightsOccupants(flightOccupants)
  }, [rooms])
  // Only show flights if: (i) they're enabled at the (brand/)property-level; and (ii) either we
  // _have_ to sell packaged rates or the selected room occupancies are valid for flights.
  const shouldShowFlights = property.areFlightsEnabled && (property.mustBundleWithFlights || isValidFlightsOccupancy)
  const destinationAirport = useMemo(() => airports.find(({ code }) => code === destinationAirportCode), [airports, destinationAirportCode])
  const isAuDomesticFlight = useMemo(() => isAuDomesticAirportRoute({
    airports,
    departureAirportCode: airport?.code,
    destinationAirportCode,
  }), [airport?.code, airports, destinationAirportCode])
  const showFlightInformation = destinationAirport &&
    (originAirports.length > 1 || (originAirports.length > 0 && destinationAirport.code !== airport?.code)) &&
    shouldShowFlights

  const shouldShowDurationTabs = useMemo(
    () => shouldShowPropertyDurationTabs(durationOptions),
    [durationOptions],
  )
  const onBeforeDurationClick = useCallback((duration: number) => {
    if (!(areAnytimeRates && shouldShowPropertyAnytimeRates)) return true
    if (duration !== property.marketedLos) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
      return false
    }
    return true
  }, [areAnytimeRates, property.marketedLos, shouldShowPropertyAnytimeRates, stateDispatch])

  // Disable quick filters if: (i) we're even remotely dealing with multiple rooms; or (ii) we have
  // an active dynamic Flash campaign promotion (as we expect these to have a curated number of
  // room rates).
  const shouldShowQuickFilters = rooms.length <= 1 && property.promotion?.type !== 'dynamicFlash'

  const showDealBanner = airport && destinationAirport
  const showFlightDealBanner = !!showDealBanner && config.FLIGHT_ENABLED && !isAgentHubEnabled && !checkInDate
  const showPromotionNudgeBanner = !!property.promotion
  const onExtendStay = useCallback((newCheckOutIsoDate: string) => {
    stateDispatch({ type: OfferPageActions.setCheckOutDate, checkOut: newCheckOutIsoDate })
  }, [stateDispatch])
  const showOfferInfoBanner = !!offerContentTags[0]?.name || showFlightDealBanner || showPromotionNudgeBanner

  const desktopFiltersWrapperRef = useRef<HTMLDivElement>(null)
  const offerType = isAgentHubEnabled && isBedbankOffer(offer) ? 'bedbank_hotel' : 'hotel'

  const shareConfig = useShareModalConfig({
    image: offer.images[0],
    offerId: offer.id,
    location: [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', '),
    type: 'offer',
    utmCampaign: 'webshare',
    queryString,
    offerType,
  })

  // Do not show the Expired recommendation when it comes from my escapes
  const shouldShowLere = useMemo(() => hasOfferExpired(offer) && !prevPath.includes('/my-escapes/order'), [offer, prevPath])

  return <ProductPaletteProvider product={offer}>
    <SocialProofDialog
      copyVariant={SOCIAL_PROOF_COPY_VARIANTS.sppStay}
      experiment={Experiments.croSocialProofSpp}
      offerImage={socialProofImage}
      recentPurchases={socialProofRecentPurchases}
    />
    <TripGuard>
      <ImmersiveSaveButtonPortal>
        <BookmarkButton offer={offer} />
      </ImmersiveSaveButtonPortal>
    </TripGuard>
    <RelativeBlock onSubmit={(e) => e.preventDefault()}>
      {useNewMobileBookingFlow && <AccommodationOfferPageStickyHeaderMobile>
        <AlwaysOnMobileStickyHeaderFilters />
      </AccommodationOfferPageStickyHeaderMobile>}
      <div>
        <CSSBreakpoint min="tablet" dynamic>
          {!showMultiRoomBookingSection && <PropertyStickyHeader
            property={property}
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
          />}
        </CSSBreakpoint>
        {shouldShowLere && (
          <div>
            <ExpiredDealsLereRecommendations hideOffers={!!activeFilter} />
          </div>
        )}
        {availableForPurchase && (
          <WalledContent enforced={property.walledGarden}>
            <Breakpoint max="mobile">
              <ModalProvider>
                <PropertySidebarFilters
                  onViewPackageOptions={scrollToPackageOptions}
                  capacities={occupancyLimits}
                  durationOptionsOverride={durationOptions}
                />
              </ModalProvider>
            </Breakpoint>
          </WalledContent>
        )}
        <OfferPageGallery
          offer={offer}
          property={property}
          shareModalConfig={shareConfig}
          autoPlayVideo={!!offer.isVideoVimeoHero}
          propertyRooms={propertyRooms}
          onViewPackageOptions={scrollToPackageOptions}
        />
        <Pane>
          <PropertyIntro
            property={property}
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
            shareConfig={shareConfig}
          />
        </Pane>
      </div>
      <ScrollAnchor ref={packageOptionsAnchor} />
      <SoftOfferPageWalledGarden>
        <PackageOptionsPane id="Package-Options">
          <ResponsivePackageOptionsSpacer>
            <LayoutContainer data-large-gap>
              <VerticalSpacer gap={32}>
                {availableForPurchase && (
                  <WalledContent enforced={property.walledGarden}>
                    <>
                      {!useNewMobileBookingFlow && (
                        <CSSBreakpoint max="mobile" dynamic>
                          <Group direction="vertical" gap={16}>
                            {/* We've temporarily disabled `cro_lpc_new_mobile_booking`, so we're on
                            the old mobile flow. However, we're not showing hotel-only and
                            hotel-with-flights pricing side-by-side, so we need this toggle to be
                            present. */}
                            {!showMultiRoomBookingSection && <SinglePropertyHotelFlightsToggleButton />}
                            <Group direction="vertical" gap={8}>
                              <OfferCapacityWithAgesSelectBox />
                              <OfferDatePickerSelectBox />
                              {showFlightInformation && flights && <OfferAirportSelectBox />}
                            </Group>
                          </Group>
                        </CSSBreakpoint>
                      )}
                      <CSSBreakpoint min="tablet" dynamic>
                        <ScrollAnchor ref={desktopFilterBlockRef} vOffset={rem(12)} />
                        <FlexDesktopFilterBlock ref={desktopFiltersWrapperRef} direction="horizontal" gap={8} horizontalAlign="center" wrap="wrap" className={clsx({ open: !!activeFilter })}>
                          <StyledOfferCapacityWithAgesSelect boundaryRef={desktopFiltersWrapperRef} capacities={occupancyLimits} />
                          <StyledOfferDatePickerSelect boundaryRef={desktopFiltersWrapperRef} />
                          {property.areFlightsEnabled && currentRegionHasFlights && <OfferDesktopFlightToggle
                            bundledWithFlightsOnly={property.mustBundleWithFlights}
                            isAuDomesticFlight={isAuDomesticFlight}
                          />}
                          <Backdrop visible={!!activeFilter && !isMobileDevice} data-testid="filter-backdrop" />
                        </FlexDesktopFilterBlock>
                      </CSSBreakpoint>
                    </>
                  </WalledContent>
                )}
              </VerticalSpacer>
            </LayoutContainer>
            <OfferPageLoyaltyPointsSlider InnerWrapper={ResponsiveSliderInnerWrapper} />
            {shouldShowDurationTabs && !useNewMobileBookingFlow && <LayoutContainer data-large-gap>
              <VerticalSpacer gap={24}>
                <VerticalSpacer gap={8}>
                  <BodyText variant="large" align="center">
                    {intl.formatMessage({
                      id: 'J45fSGD5me9d0j5jTAfG',
                      defaultMessage: 'Select duration',
                      description: 'Text label for duration selection section on property page',
                    })}
                  </BodyText>
                  <OfferDurationTabs
                    durationOptionsOverride={durationOptions}
                    onBeforeDurationClick={onBeforeDurationClick}
                    availableForPurchaseOverride={availableForPurchase}
                  />
                </VerticalSpacer>
              </VerticalSpacer>
            </LayoutContainer>}
            {showOfferInfoBanner && <LayoutContainer data-large-gap>
              <VerticalSpacer gap={16}>
                <ContentTagBanner offerId={offer.id} page="offer" />
                {showFlightDealBanner && <OfferPageFlightDealBanner
                  destinationAirportCode={destinationAirport.code}
                  originAirportCode={airport.code}
                />}
                {showPromotionNudgeBanner && <PropertyPromotionNudgeBanner onExtendStay={onExtendStay} />}
              </VerticalSpacer>
            </LayoutContainer>}
            {showMultiRoomBookingSection && <LayoutContainer>
              <VerticalSpacer gap={32}>
                {/* TODO: per-room RateImpression events in individual mode (V1 ships zero impressions; conversions still fire per-room). */}
                <PropertyMultiRoomBookingSectionDesktop />
                {showIndividualRoomGrid && <PropertyDesktopIndividualRooms />}
              </VerticalSpacer>
            </LayoutContainer>}
            {shouldShowQuickFilters && <QuickFiltersRoomsGroup direction="vertical" gap={0} tabletGap={32} horizontalAlign="stretch">
              <FullBleedOnMobileContainer>
                <PropertyQuickFilters />
              </FullBleedOnMobileContainer>
              <FullBleedOnMobileContainer>
                {offer.packages.length > 0 && !useNewMobileBookingFlow && <PropertyRoomOptions
                  onViewPackageOptions={scrollToPackageOptions}
                  flushTop
                />}
              </FullBleedOnMobileContainer>
            </QuickFiltersRoomsGroup>}
            {!showIndividualRoomGrid && !shouldShowQuickFilters && <FullBleedOnMobileContainer>
              {offer.packages.length > 0 && !useNewMobileBookingFlow && <PropertyRoomOptions
                onViewPackageOptions={scrollToPackageOptions}
              />}
            </FullBleedOnMobileContainer>}
            {showFlightInformation && <LayoutContainer>
              <Caption variant="medium" align="center">
                {intl.formatMessage(
                  {
                    id: 'V6oeW3V4xUi9qn30TFXQ',
                    defaultMessage: '*Price is indicative and based on sample return fares (inclusive of booking fees on Low Cost Carriers). To view live pricing, conduct a full search. {brandName} full Terms and Conditions apply.',
                    description: 'Flight pricing disclaimer text in the flight search or booking flow',
                  },
                  { brandName: config.brandConfig.name },
                )}
              </Caption>
            </LayoutContainer>}
          </ResponsivePackageOptionsSpacer>
        </PackageOptionsPane>
        {!showMultiRoomBookingSection && !useNewMobileBookingFlow && availableForPurchase &&
          <PropertyStickyFooter
            property={property}
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
          />}
        {!showMultiRoomBookingSection && useNewMobileBookingFlow && availableForPurchase && shouldMountMobileAccommodationStickyFooter && (
          <AccommodationOfferPageStickyFooter />
        )}
      </SoftOfferPageWalledGarden>
    </RelativeBlock>
    <SoftOfferPageWalledGarden bypassWall={isWalledGardenEuropeOffersEnabled} wall={<OfferPageSignUpWall offerType="hotel" saveUpToPercent={loggedOutSavedPercent} />} />
    <ErrorBoundary fallback={null}>
      <PropertyUniqueStaysRecommendations />
    </ErrorBoundary>
  </ProductPaletteProvider>
}

type MappedProps = Pick<Props,
  | 'airport'
  | 'duration'
  | 'activeFilter'
  | 'rooms'
  | 'checkInDate'
  | 'checkOutDate'
  | 'flights'
  | 'areAnytimeRates'
  | 'shouldShowPropertyAnytimeRates'
  | 'durationOptions'
  | 'availableForPurchase'
  | 'loggedOutSavedPercent'
  | 'multipleRoomBookingActive'
  | 'multiRoomMode'
  | 'activeHeaderFilter'
  | 'utmContent'
  | 'isAlternativeDatesFlow'
  | 'hasOpenFilter'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  airport: state.airport,
  duration: state.duration,
  activeFilter: state.activeFilter,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  checkOutDate: getCheckoutDate(state),
  flights: state.flights,
  areAnytimeRates: !getPropertyRatesKey(state, appState),
  shouldShowPropertyAnytimeRates: getShouldShowPropertyAnytimeRate(state, appState),
  durationOptions: getDurationOptions(state),
  availableForPurchase: isPropertyAvailableForPurchase(state, appState),
  loggedOutSavedPercent: getPropertyLoggedOutSavedPercent(state, appState),
  multipleRoomBookingActive: isSppMultipleRoomBookingActive(state, appState),
  multiRoomMode: state.multiRoomMode,
  activeHeaderFilter: state.activeHeaderFilter,
  utmContent: appState.router.location.query.utm_content,
  isAlternativeDatesFlow: !!state.isAlternativeDatesFlow,
  hasOpenFilter: !!state.activeFilter,
}), PropertyContent)
